import { useRef, useState } from 'react';
import * as THREE from 'three';
import { RoundedBox, Edges, Html } from '@react-three/drei';
import type { ThreeEvent } from '@react-three/fiber';

interface ContainerBoxProps {
  positionX: number;
  positionY: number;
  positionZ: number;
  lengthCm: number;
  widthCm: number;
  heightCm: number;
  color: string;
  isSelected: boolean;
  onClick: () => void;
  label?: string;
}

export function ContainerBox({
  positionX,
  positionY,
  positionZ,
  lengthCm,
  widthCm,
  heightCm,
  color,
  isSelected,
  onClick,
  label,
}: ContainerBoxProps) {
  const meshRef = useRef<THREE.Mesh>(null);
  const [hovered, setHovered] = useState(false);

  // Convert cm to meters
  const l = lengthCm / 100;
  const w = widthCm / 100;
  const h = heightCm / 100;
  const x = positionX / 100;
  const y = positionZ / 100; // Z in packing = Y in 3D (height)
  const z = positionY / 100; // Y in packing = Z in 3D (depth)

  return (
    <RoundedBox
      ref={meshRef}
      args={[l, h, w]}
      radius={0.005}
      smoothness={4}
      position={[x + l / 2, y + h / 2, z + w / 2]}
      castShadow
      receiveShadow
      onClick={(e: ThreeEvent<MouseEvent>) => {
        e.stopPropagation();
        onClick();
      }}
      onPointerOver={() => setHovered(true)}
      onPointerOut={() => setHovered(false)}
    >
      <meshStandardMaterial
        color={color}
        opacity={isSelected ? 1.0 : hovered ? 0.95 : 0.92}
        transparent={isSelected || hovered}
        metalness={0.1}
        roughness={0.7}
      />
      {/* Edges siempre visibles para que las cajas chicas se distingan */}
      <Edges
        color={isSelected ? '#ffffff' : hovered ? '#e2e8f0' : '#00000030'}
        lineWidth={isSelected ? 2 : 1}
      />

      {/* Tooltip al hacer hover - muestra descripción y dimensiones */}
      {(hovered || isSelected) && label && (
        <Html
          position={[0, h / 2 + 0.05, 0]}
          center
          distanceFactor={6}
          style={{ pointerEvents: 'none' }}
        >
          <div className="bg-black/80 text-white px-2 py-1 rounded text-[10px] whitespace-nowrap select-none font-medium">
            {label}
            <span className="text-white/50 ml-1.5">
              {lengthCm}×{widthCm}×{heightCm}cm
            </span>
          </div>
        </Html>
      )}
    </RoundedBox>
  );
}
